/**
 * Report & Analytics Service
 *
 * Data-access layer for every report and analytics endpoint in the reports
 * contract. Backed by the normalized shapes in `../types/domain/analytics.types`
 * and returns them ready to consume.
 *
 * CASING NORMALIZATION
 * --------------------
 * Some upstream responses arrive with camelCase keys and others with
 * snake_case. Every JSON response returned by this service is passed through
 * {@link toSnakeCaseDeep} first, so callers always receive uniform snake_case
 * regardless of the source casing (see the types file for the convention).
 */

import { apiClient } from './api/client';
import { ApiErrorClass } from '../types/api/errors';
import {
  downloadBlob,
  filenameFromContentDisposition,
} from '../utils/file-download';
import { toClientPath } from '../utils/notification.utils';
import {
  validateAnalytics,
  type AnalyticsInput,
} from '../validation/common.schemas';

import type {
  ApiCreated,
  ApiQueued,
  BodyMeasurementExportQuery,
  CelebrationRequest,
  ChallengeAnalyticsResponse,
  ClientRosterExportQuery,
  ConsistencyResponse,
  DeloadStatus,
  ExerciseCurve,
  ExerciseCurveQuery,
  ExerciseCurvesCollection,
  ExerciseFrequencyResult,
  ExerciseProgressResult,
  MuscleGroupCoverageResult,
  TrainingLoadAvailability,
  FileExportResult,
  GoalProgressReport,
  GoalTrajectory,
  HealthTrends,
  HowYouTrain,
  MonthlyRecap,
  MonthlyRecapQuery,
  MuscleGroupBalanceResult,
  Periodization,
  PeriodQuery,
  PreferredTimesResult,
  PreSessionBrief,
  ProgramEfficacy,
  ProgressionAdviceIndex,
  ProgressionAdviceItem,
  ProgressionAdviceQuery,
  ProgressReport,
  ProgressReportEmailBody,
  ProgressReportOptions,
  ProgressReportQuery,
  ProgressReportStored,
  ProgressReportStoreBody,
  RecoveryStatus,
  ReportExportQuery,
  StrengthReport,
  StreaksResult,
  TodayView,
  TrainerRosterReport,
  TrainingIntensityResult,
  VolumeResponse,
  WeeklyTrainingLoads,
  WorkoutFrequencyResult,
} from '../types/domain/analytics.types';
import type { NotificationGenerateBlock } from '../types/notification.types';

// =============================================================================
// CASE TRANSFORMATION
// =============================================================================

/**
 * Convert a single object KEY from camelCase to snake_case.
 *
 * Only keys that contain a camelCase boundary — a lowercase letter immediately
 * followed by an uppercase letter — are converted; every other key is returned
 * untouched. This deliberately preserves keys that are *data* rather than field
 * names: weekday names ("Monday"), muscle groups ("Chest"), time slots
 * ("Morning"), rating labels, period codes ("7d") and exercise guids (including
 * alphanumeric/ULID-style ids like "01ARZ3NDEK…") would all be corrupted by a
 * blanket lower-casing transform. The boundary is intentionally not triggered
 * by a digit→uppercase transition, so those alphanumeric ids stay intact — no
 * real field name relies on that (numeric fields are already snake_case).
 */
export function toSnakeCaseKey(key: string): string {
  if (!/[a-z][A-Z]/.test(key)) return key;
  return key.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
}

/**
 * Recursively rewrite every object key in a JSON value to snake_case using
 * {@link toSnakeCaseKey}. Arrays and primitives pass through unchanged; only
 * plain-object keys are rewritten (values are never touched). Idempotent on
 * already-snake_case payloads.
 */
export function toSnakeCaseDeep<T = unknown>(input: unknown): T {
  if (Array.isArray(input)) {
    return input.map((item) => toSnakeCaseDeep(item)) as unknown as T;
  }

  if (input !== null && typeof input === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(
      input as Record<string, unknown>
    )) {
      result[toSnakeCaseKey(key)] = toSnakeCaseDeep(value);
    }
    return result as T;
  }

  return input as T;
}

// =============================================================================
// CLIENT-OWNED COPY
// =============================================================================

/**
 * Shown when an export is queued (`202`). The request is sent with
 * `responseType: 'blob'`, so a 202's JSON body arrives as binary; rather than
 * parse it back out for generic boilerplate, the frontend owns this copy.
 */
export const QUEUED_REPORT_MESSAGE =
  'Your report is being generated. You will be notified when it is ready to download.';

// =============================================================================
// NORMALIZATION OPT-OUT
// =============================================================================

/**
 * Request-path fragments whose responses SKIP the deep snake_case transform.
 *
 * Normalization is uniform and ON by default, so every endpoint — including new
 * ones — is covered automatically. This is the single opt-out switch: add a
 * fragment here (matched as a substring of the request path) to skip the
 * transform for an endpoint whose payload is already snake_case on the wire and
 * large enough that the clone shows up as a *measured* hot spot.
 *
 * Intentionally empty until profiling names a specific offender — flip the
 * switch by adding its route fragment, e.g.
 * `['/analytics/exercise-curves', '/reports/client-roster']`.
 */
export const NORMALIZATION_OPT_OUT: readonly string[] = [];

// =============================================================================
// SERVICE CLASS
// =============================================================================

export class ReportService {
  private readonly baseUrl = '/user';

  private userBase(username: string): string {
    return `${this.baseUrl}/${username}`;
  }

  // ---------------------------------------------------------------------------
  // 2. ANALYTICS — METRIC ENGINE
  // ---------------------------------------------------------------------------

  /** Volume analytics. */
  async getVolume(
    username: string,
    params?: AnalyticsInput
  ): Promise<VolumeResponse> {
    return this.getAnalytics<VolumeResponse>(username, 'volume', params);
  }

  /** Intensity analytics. */
  async getIntensity(
    username: string,
    params?: AnalyticsInput
  ): Promise<TrainingIntensityResult> {
    return this.getAnalytics<TrainingIntensityResult>(
      username,
      'intensity',
      params
    );
  }

  /** Workout-frequency analytics. */
  async getFrequency(
    username: string,
    params?: AnalyticsInput
  ): Promise<WorkoutFrequencyResult> {
    return this.getAnalytics<WorkoutFrequencyResult>(
      username,
      'frequency',
      params
    );
  }

  /** Exercise-frequency analytics. */
  async getExerciseFrequency(
    username: string,
    params?: AnalyticsInput
  ): Promise<ExerciseFrequencyResult> {
    return this.getAnalytics<ExerciseFrequencyResult>(
      username,
      'exercise-frequency',
      params
    );
  }

  /** Workout-consistency analytics. */
  async getWorkoutConsistency(
    username: string,
    params?: AnalyticsInput
  ): Promise<ConsistencyResponse> {
    return this.getAnalytics<ConsistencyResponse>(
      username,
      'workout-consistency',
      params
    );
  }

  /** Muscle-group balance analytics. */
  async getMuscleGroupBalance(
    username: string,
    params?: AnalyticsInput
  ): Promise<MuscleGroupBalanceResult> {
    return this.getAnalytics<MuscleGroupBalanceResult>(
      username,
      'muscle-group-balance',
      params
    );
  }

  /** Workout-streak analytics. */
  async getWorkoutStreaks(
    username: string,
    params?: AnalyticsInput
  ): Promise<StreaksResult> {
    return this.getAnalytics<StreaksResult>(
      username,
      'workout-streaks',
      params
    );
  }

  /** Preferred-workout-times analytics. */
  async getPreferredWorkoutTimes(
    username: string,
    params?: AnalyticsInput
  ): Promise<PreferredTimesResult> {
    return this.getAnalytics<PreferredTimesResult>(
      username,
      'preferred-workout-times',
      params
    );
  }

  /**
   * Weekly major-muscle-group coverage. The route keeps its historical
   * `exercise-variety` name; the payload is the coverage rule.
   */
  async getExerciseVariety(
    username: string,
    params?: AnalyticsInput
  ): Promise<MuscleGroupCoverageResult> {
    return this.getAnalytics<MuscleGroupCoverageResult>(
      username,
      'exercise-variety',
      params
    );
  }

  /**
   * Challenge analytics. The response carries an envelope-level `metadata` block
   * alongside `data`, so the whole envelope is returned.
   */
  async getChallengeAnalytics(
    username: string,
    params?: AnalyticsInput
  ): Promise<ChallengeAnalyticsResponse> {
    try {
      this.validateUsername(username);
      this.validateAnalyticsParams(params);
      return await this.fetchData<ChallengeAnalyticsResponse>(
        `${this.userBase(username)}/analytics/challenges`,
        params,
        'raw'
      );
    } catch (error) {
      throw this.createReportError(
        error,
        'Failed to fetch challenge analytics'
      );
    }
  }

  /** Per-exercise progress. Unknown guid resolves to a 404 error. */
  async getExerciseProgress(
    username: string,
    exerciseGuid: string,
    params?: AnalyticsInput
  ): Promise<ExerciseProgressResult> {
    try {
      this.validateUsername(username);
      this.validateResourceId(exerciseGuid, 'Exercise guid');
      this.validateAnalyticsParams(params);
      return await this.fetchData<ExerciseProgressResult>(
        `${this.userBase(username)}/exercise/${exerciseGuid}/progress`,
        params
      );
    } catch (error) {
      throw this.createReportError(error, 'Failed to fetch exercise progress');
    }
  }

  // ---------------------------------------------------------------------------
  // 3. ANALYTICS — PERIODIZATION, CURVES, ADVICE, RECOVERY
  // ---------------------------------------------------------------------------

  /** Periodization status. */
  async getPeriodization(username: string): Promise<Periodization> {
    try {
      this.validateUsername(username);
      return await this.fetchData<Periodization>(
        `${this.userBase(username)}/analytics/periodization`
      );
    } catch (error) {
      throw this.createReportError(error, 'Failed to fetch periodization');
    }
  }

  /**
   * Training-load availability. The route keeps its `fatigue` name; the
   * score it was named for is gone and only the availability block remains.
   */
  async getFatigueStatus(username: string): Promise<TrainingLoadAvailability> {
    try {
      this.validateUsername(username);
      return await this.fetchData<TrainingLoadAvailability>(
        `${this.userBase(username)}/analytics/periodization/fatigue`
      );
    } catch (error) {
      throw this.createReportError(error, 'Failed to fetch fatigue status');
    }
  }

  /**
   * The weekly session-RPE load series. The route keeps its `workload-ratio`
   * name; the ratio is gone and the payload is the series it came from.
   */
  async getWorkloadRatio(username: string): Promise<WeeklyTrainingLoads> {
    try {
      this.validateUsername(username);
      return await this.fetchData<WeeklyTrainingLoads>(
        `${this.userBase(username)}/analytics/periodization/workload-ratio`
      );
    } catch (error) {
      throw this.createReportError(error, 'Failed to fetch workload ratio');
    }
  }

  /** Deload status. */
  async getDeloadStatus(username: string): Promise<DeloadStatus> {
    try {
      this.validateUsername(username);
      return await this.fetchData<DeloadStatus>(
        `${this.userBase(username)}/analytics/periodization/deload`
      );
    } catch (error) {
      throw this.createReportError(error, 'Failed to fetch deload status');
    }
  }

  /** Exercise progression curves (all exercises). */
  async getExerciseCurves(
    username: string,
    params?: ExerciseCurveQuery
  ): Promise<ExerciseCurvesCollection> {
    try {
      this.validateUsername(username);
      return await this.fetchData<ExerciseCurvesCollection>(
        `${this.userBase(username)}/analytics/exercise-curves`,
        params
      );
    } catch (error) {
      throw this.createReportError(error, 'Failed to fetch exercise curves');
    }
  }

  /** Stalled subset of the exercise progression curves. */
  async getStalledExerciseCurves(
    username: string,
    params?: ExerciseCurveQuery
  ): Promise<ExerciseCurvesCollection> {
    try {
      this.validateUsername(username);
      return await this.fetchData<ExerciseCurvesCollection>(
        `${this.userBase(username)}/analytics/exercise-curves/stalled`,
        params
      );
    } catch (error) {
      throw this.createReportError(
        error,
        'Failed to fetch stalled exercise curves'
      );
    }
  }

  /** A single exercise progression curve. Unknown guid resolves to a 404 error. */
  async getExerciseCurve(
    username: string,
    exerciseGuid: string,
    params?: ExerciseCurveQuery
  ): Promise<ExerciseCurve> {
    try {
      this.validateUsername(username);
      this.validateResourceId(exerciseGuid, 'Exercise guid');
      return await this.fetchData<ExerciseCurve>(
        `${this.userBase(username)}/analytics/exercise-curves/${exerciseGuid}`,
        params
      );
    } catch (error) {
      throw this.createReportError(error, 'Failed to fetch exercise curve');
    }
  }

  /**
   * Progression advice for every analyzed exercise. The collection carries its
   * own `meta` block, so the whole envelope is returned.
   */
  async getProgressionAdvice(
    username: string,
    params?: ProgressionAdviceQuery
  ): Promise<ProgressionAdviceIndex> {
    try {
      this.validateUsername(username);
      return await this.fetchData<ProgressionAdviceIndex>(
        `${this.userBase(username)}/analytics/progression-advice`,
        params,
        'raw'
      );
    } catch (error) {
      throw this.createReportError(error, 'Failed to fetch progression advice');
    }
  }

  /**
   * Progression advice for a single exercise. Insufficient data resolves to a
   * 404 error.
   */
  async getProgressionAdviceForExercise(
    username: string,
    exerciseGuid: string
  ): Promise<ProgressionAdviceItem> {
    try {
      this.validateUsername(username);
      this.validateResourceId(exerciseGuid, 'Exercise guid');
      return await this.fetchData<ProgressionAdviceItem>(
        `${this.userBase(username)}/analytics/progression-advice/${exerciseGuid}`
      );
    } catch (error) {
      throw this.createReportError(error, 'Failed to fetch progression advice');
    }
  }

  /** Recovery status. */
  async getRecoveryStatus(username: string): Promise<RecoveryStatus> {
    try {
      this.validateUsername(username);
      return await this.fetchData<RecoveryStatus>(
        `${this.userBase(username)}/analytics/recovery-status`
      );
    } catch (error) {
      throw this.createReportError(error, 'Failed to fetch recovery status');
    }
  }

  // ---------------------------------------------------------------------------
  // 4. REPORTS — JSON
  // ---------------------------------------------------------------------------

  /** Strength report (personal records). */
  async getStrengthReport(username: string): Promise<StrengthReport> {
    try {
      this.validateUsername(username);
      return await this.fetchData<StrengthReport>(
        `${this.userBase(username)}/reports/personal-records`
      );
    } catch (error) {
      throw this.createReportError(error, 'Failed to fetch strength report');
    }
  }

  /** Goal trajectory report. */
  async getGoalTrajectory(username: string): Promise<GoalTrajectory> {
    try {
      this.validateUsername(username);
      return await this.fetchData<GoalTrajectory>(
        `${this.userBase(username)}/reports/goal-trajectory`
      );
    } catch (error) {
      throw this.createReportError(error, 'Failed to fetch goal trajectory');
    }
  }

  /** Today view. */
  async getToday(username: string): Promise<TodayView> {
    try {
      this.validateUsername(username);
      return await this.fetchData<TodayView>(
        `${this.userBase(username)}/reports/today`
      );
    } catch (error) {
      throw this.createReportError(error, "Failed to fetch today's view");
    }
  }

  /** Pre-session brief. */
  async getPreSessionBrief(username: string): Promise<PreSessionBrief> {
    try {
      this.validateUsername(username);
      return await this.fetchData<PreSessionBrief>(
        `${this.userBase(username)}/reports/pre-session-brief`
      );
    } catch (error) {
      throw this.createReportError(error, 'Failed to fetch pre-session brief');
    }
  }

  /** Monthly recap (completed months only; defaults to the last complete month). */
  async getMonthlyRecap(
    username: string,
    params?: MonthlyRecapQuery
  ): Promise<MonthlyRecap> {
    try {
      this.validateUsername(username);
      return await this.fetchData<MonthlyRecap>(
        `${this.userBase(username)}/reports/monthly-recap`,
        params
      );
    } catch (error) {
      throw this.createReportError(error, 'Failed to fetch monthly recap');
    }
  }

  /** Training-style / intensity report. */
  async getHowYouTrain(
    username: string,
    params?: PeriodQuery
  ): Promise<HowYouTrain> {
    try {
      this.validateUsername(username);
      return await this.fetchData<HowYouTrain>(
        `${this.userBase(username)}/reports/how-you-train`,
        params
      );
    } catch (error) {
      throw this.createReportError(
        error,
        'Failed to fetch how-you-train report'
      );
    }
  }

  /** Health-trends report. */
  async getHealthTrends(
    username: string,
    params?: PeriodQuery
  ): Promise<HealthTrends> {
    try {
      this.validateUsername(username);
      return await this.fetchData<HealthTrends>(
        `${this.userBase(username)}/reports/health-trends`,
        params
      );
    } catch (error) {
      throw this.createReportError(error, 'Failed to fetch health trends');
    }
  }

  /** Program-efficacy report (trainer-owned). */
  async getProgramEfficacy(username: string): Promise<ProgramEfficacy> {
    try {
      this.validateUsername(username);
      return await this.fetchData<ProgramEfficacy>(
        `${this.userBase(username)}/reports/program-efficacy`
      );
    } catch (error) {
      throw this.createReportError(error, 'Failed to fetch program efficacy');
    }
  }

  /** Trainer roster report (trainer-owned). */
  async getClientRoster(username: string): Promise<TrainerRosterReport> {
    try {
      this.validateUsername(username);
      return await this.fetchData<TrainerRosterReport>(
        `${this.userBase(username)}/reports/client-roster`
      );
    } catch (error) {
      throw this.createReportError(error, 'Failed to fetch client roster');
    }
  }

  /** Progress-report options / metadata (no query params). */
  async getProgressReportOptions(
    username: string
  ): Promise<ProgressReportOptions> {
    try {
      this.validateUsername(username);
      return await this.fetchData<ProgressReportOptions>(
        `${this.userBase(username)}/reports/progress`
      );
    } catch (error) {
      throw this.createReportError(
        error,
        'Failed to fetch progress report options'
      );
    }
  }

  /**
   * Generate a progress report for the given period / metrics.
   *
   * `/reports/progress/generate`, not `/reports/progress` with a query: the bare
   * route is the options index and ignores the query, so a report requested
   * there comes back as options (the id segment was dropped and `show` moved to
   * `generate` on 2026-08-07).
   */
  async getProgressReport(
    username: string,
    params: ProgressReportQuery
  ): Promise<ProgressReport> {
    try {
      this.validateUsername(username);
      return await this.fetchData<ProgressReport>(
        `${this.userBase(username)}/reports/progress/generate`,
        params
      );
    } catch (error) {
      throw this.createReportError(error, 'Failed to generate progress report');
    }
  }

  /** Generate and cache a progress report; returns a trimmed summary. */
  async storeProgressReport(
    username: string,
    body: ProgressReportStoreBody
  ): Promise<ProgressReportStored> {
    try {
      this.validateUsername(username);
      const endpoint = `${this.userBase(username)}/reports/progress`;
      const response = await apiClient.post(endpoint, body);
      return this.unwrapPayload<ProgressReportStored>(
        endpoint,
        response.data,
        response.status
      );
    } catch (error) {
      throw this.createReportError(error, 'Failed to store progress report');
    }
  }

  /** Clear the cached progress report. */
  async deleteProgressReport(username: string): Promise<void> {
    try {
      this.validateUsername(username);
      await apiClient.delete(`${this.userBase(username)}/reports/progress`);
    } catch (error) {
      throw this.createReportError(error, 'Failed to delete progress report');
    }
  }

  /** Goal-specific progress report. */
  async getGoalProgress(
    username: string,
    goalId: string
  ): Promise<GoalProgressReport> {
    try {
      this.validateUsername(username);
      this.validateResourceId(goalId, 'Goal id');
      return await this.fetchData<GoalProgressReport>(
        `${this.userBase(username)}/goals/${goalId}/progress`
      );
    } catch (error) {
      throw this.createReportError(error, 'Failed to fetch goal progress');
    }
  }

  // ---------------------------------------------------------------------------
  // 5. REPORTS — FILE EXPORTS
  // ---------------------------------------------------------------------------

  /** Export workout history (PDF or CSV). */
  async exportWorkoutHistory(
    username: string,
    params?: ReportExportQuery
  ): Promise<FileExportResult> {
    return this.export(
      username,
      `${this.userBase(username)}/workout-history/export`,
      params,
      'Failed to export workout history'
    );
  }

  /** Export the progress report (PDF). */
  async exportProgressReport(
    username: string,
    params?: ReportExportQuery
  ): Promise<FileExportResult> {
    return this.export(
      username,
      `${this.userBase(username)}/progress-report/export`,
      params,
      'Failed to export progress report'
    );
  }

  /** Export a goal-specific progress report (PDF). */
  async exportGoalProgressReport(
    username: string,
    goalId: string,
    params?: ReportExportQuery
  ): Promise<FileExportResult> {
    this.validateResourceId(goalId, 'Goal id');
    return this.export(
      username,
      `${this.userBase(username)}/progress-report/${goalId}/export`,
      params,
      'Failed to export goal progress report'
    );
  }

  /** Export the executive summary (PDF). */
  async exportExecutiveSummary(
    username: string,
    params?: ReportExportQuery
  ): Promise<FileExportResult> {
    return this.export(
      username,
      `${this.userBase(username)}/executive-summary/export`,
      params,
      'Failed to export executive summary'
    );
  }

  /** Export the report for all challenges (PDF). */
  async exportAllChallengesReport(
    username: string,
    params?: ReportExportQuery
  ): Promise<FileExportResult> {
    return this.export(
      username,
      `${this.userBase(username)}/challenge-report/export`,
      params,
      'Failed to export challenges report'
    );
  }

  /** Export the report for a single challenge (PDF). */
  async exportChallengeReport(
    username: string,
    challengeId: string,
    params?: ReportExportQuery
  ): Promise<FileExportResult> {
    this.validateResourceId(challengeId, 'Challenge id');
    return this.export(
      username,
      `${this.userBase(username)}/challenge-report/${challengeId}/export`,
      params,
      'Failed to export challenge report'
    );
  }

  /** Export a challenge participant report (PDF). */
  async exportChallengeParticipantReport(
    username: string,
    participantId: string,
    params?: ReportExportQuery
  ): Promise<FileExportResult> {
    this.validateResourceId(participantId, 'Challenge participant id');
    return this.export(
      username,
      `${this.userBase(username)}/challenge-participant-report/${participantId}/export`,
      params,
      'Failed to export challenge participant report'
    );
  }

  /** Export the trainer client roster (PDF or CSV; always streams). */
  async exportClientRoster(
    username: string,
    params?: ClientRosterExportQuery
  ): Promise<FileExportResult> {
    return this.export(
      username,
      `${this.userBase(username)}/client-roster/export`,
      params,
      'Failed to export client roster'
    );
  }

  /**
   * Export body measurements (CSV; always streams). `measurement` (a slug) is
   * required.
   */
  async exportBodyMeasurements(
    username: string,
    params: BodyMeasurementExportQuery
  ): Promise<FileExportResult> {
    try {
      this.validateUsername(username);
      this.validateResourceId(params?.measurement, 'Measurement');
      return await this.handleFileExport(
        `${this.userBase(username)}/body-measurements/export`,
        params
      );
    } catch (error) {
      throw this.createReportError(error, 'Failed to export body measurements');
    }
  }

  // ---------------------------------------------------------------------------
  // 6. REPORTS — MUTATIONS
  // ---------------------------------------------------------------------------

  /**
   * Email the authenticated user their progress report for `period` (the last
   * 90 days without one). Fire-and-forget: a `202` means queued for email, not
   * sent. `username` must be the caller's own.
   */
  async emailProgressReport(
    username: string,
    body?: ProgressReportEmailBody
  ): Promise<ApiQueued> {
    try {
      this.validateUsername(username);
      const endpoint = `${this.userBase(username)}/reports/progress/email`;
      const response = await apiClient.post(endpoint, body);
      return this.normalizePayload<ApiQueued>(endpoint, response.data);
    } catch (error) {
      throw this.createReportError(
        error,
        'Failed to email your progress report'
      );
    }
  }

  /** Celebrate a client achievement (trainer-only). */
  async celebrate(body: CelebrationRequest): Promise<ApiCreated> {
    try {
      if (body?.type !== 'pr' && body?.type !== 'milestone') {
        throw new ApiErrorClass(
          'A valid celebration type is required',
          'BAD_REQUEST',
          400
        );
      }
      // The achievement id is an ID — a positive integer or a non-empty
      // (hashid) string. Reject anything else before hitting the network.
      const idIsValid =
        (typeof body.id === 'number' &&
          Number.isInteger(body.id) &&
          body.id >= 1) ||
        (typeof body.id === 'string' && body.id.trim().length > 0);
      if (!idIsValid) {
        throw new ApiErrorClass(
          'A valid achievement id is required',
          'BAD_REQUEST',
          400
        );
      }
      const endpoint = '/trainer/celebrate';
      const response = await apiClient.post(endpoint, body);
      return this.normalizePayload<ApiCreated>(endpoint, response.data);
    } catch (error) {
      throw this.createReportError(error, 'Failed to celebrate achievement');
    }
  }

  // ---------------------------------------------------------------------------
  // UTILITY
  // ---------------------------------------------------------------------------

  /**
   * Trigger a browser download of a previously fetched export stream. Delegates
   * to the shared helper so every export flow shares one implementation.
   */
  downloadFile(blob: Blob, filename: string): void {
    downloadBlob(blob, filename);
  }

  // ---------------------------------------------------------------------------
  // PRIVATE HELPERS
  // ---------------------------------------------------------------------------

  /**
   * GET an analytics endpoint that accepts the common analytics query params.
   */
  private async getAnalytics<T>(
    username: string,
    segment: string,
    params?: AnalyticsInput
  ): Promise<T> {
    this.validateUsername(username);
    this.validateAnalyticsParams(params);
    return this.fetchData<T>(
      `${this.userBase(username)}/analytics/${segment}`,
      params
    );
  }

  /**
   * GET a JSON endpoint and normalize its casing (unless the endpoint has opted
   * out via {@link NORMALIZATION_OPT_OUT}).
   *
   * @param envelope `wrapped` (default) unwraps the `{ data }` success envelope;
   *   `raw` returns the whole body (for responses that carry envelope-level
   *   `metadata` / `meta` as siblings of `data`).
   */
  private async fetchData<T>(
    endpoint: string,
    params?: unknown,
    envelope: 'wrapped' | 'raw' = 'wrapped'
  ): Promise<T> {
    const response = await apiClient.get(endpoint, {
      params: params as Record<string, unknown> | undefined,
    });

    if (envelope === 'raw') {
      const body = response.data;
      if (body === undefined || body === null) {
        throw new ApiErrorClass(
          'Invalid response format from server',
          'BAD_REQUEST',
          response.status ?? 500
        );
      }
      return this.normalizePayload<T>(endpoint, body);
    }

    return this.unwrapPayload<T>(endpoint, response.data, response.status);
  }

  /** Unwrap the `{ data }` success envelope and normalize its casing. */
  private unwrapPayload<T>(endpoint: string, body: unknown, status: number): T {
    const payload =
      body && typeof body === 'object'
        ? (body as { data?: unknown }).data
        : undefined;

    if (payload === undefined || payload === null) {
      throw new ApiErrorClass(
        'Invalid response format from server',
        'BAD_REQUEST',
        status ?? 500
      );
    }

    return this.normalizePayload<T>(endpoint, payload);
  }

  /**
   * Apply the deep snake_case transform to a payload unless its request path is
   * opted out via {@link NORMALIZATION_OPT_OUT}. Centralizes the on-by-default
   * behavior so every endpoint is normalized unless explicitly excluded.
   */
  private normalizePayload<T>(endpoint: string, payload: unknown): T {
    const optedOut = NORMALIZATION_OPT_OUT.some((fragment) =>
      endpoint.includes(fragment)
    );
    return optedOut ? (payload as T) : toSnakeCaseDeep<T>(payload);
  }

  /**
   * Run the export a notification's Generate button carries. The API built
   * the request — path and query — so it is sent exactly as given; it is the
   * ordinary export, and so streams, queues and is refused (402) like one.
   */
  async generateFromNotification(
    block: NotificationGenerateBlock
  ): Promise<FileExportResult> {
    try {
      return await this.handleFileExport(
        toClientPath(block.request.path),
        block.request.query
      );
    } catch (error) {
      throw this.createReportError(error, 'Failed to generate the report');
    }
  }

  /** Validate the username, then run a file export. */
  private async export(
    username: string,
    endpoint: string,
    params: ReportExportQuery | ClientRosterExportQuery | undefined,
    errorMessage: string
  ): Promise<FileExportResult> {
    try {
      this.validateUsername(username);
      return await this.handleFileExport(endpoint, params);
    } catch (error) {
      throw this.createReportError(error, errorMessage);
    }
  }

  /**
   * Run a file export, detecting a streamed file (`200`) vs a queued job
   * (`202`). The body is a Blob (`responseType: 'blob'`), so it is not parsed —
   * the queued copy is client-owned (see {@link QUEUED_REPORT_MESSAGE}).
   */
  private async handleFileExport(
    endpoint: string,
    params?: unknown
  ): Promise<FileExportResult> {
    const response = await apiClient.get(endpoint, {
      params: params as Record<string, unknown> | undefined,
      responseType: 'blob',
    });

    if (response.status === 202) {
      return { type: 'queued', message: QUEUED_REPORT_MESSAGE };
    }

    const filename = filenameFromContentDisposition(
      response.headers['content-disposition'],
      'report.pdf'
    );

    return { type: 'stream', data: response.data as Blob, filename };
  }

  private validateUsername(username: string): asserts username is string {
    if (!username || typeof username !== 'string' || username.trim() === '') {
      throw new ApiErrorClass('Username is required', 'BAD_REQUEST', 400);
    }
  }

  private validateResourceId(
    resourceId: string | undefined,
    fieldName: string
  ): asserts resourceId is string {
    if (
      !resourceId ||
      typeof resourceId !== 'string' ||
      resourceId.trim() === ''
    ) {
      throw new ApiErrorClass(`${fieldName} is required`, 'BAD_REQUEST', 400);
    }
  }

  private validateAnalyticsParams(params?: AnalyticsInput): void {
    if (!params) return;

    try {
      validateAnalytics(params);
    } catch (error) {
      if (error && typeof error === 'object' && 'issues' in error) {
        const zodError = error as {
          issues: Array<{ message: string; path: string[] }>;
        };
        const firstIssue = zodError.issues[0];
        const fieldName = firstIssue?.path[0] || 'parameter';
        const message = firstIssue?.message || 'Invalid parameter';
        throw new ApiErrorClass(
          `Invalid ${fieldName}: ${message}`,
          'BAD_REQUEST',
          400
        );
      }
      throw new ApiErrorClass(
        'Invalid analytics parameters',
        'BAD_REQUEST',
        400
      );
    }
  }

  private createReportError(
    error: unknown,
    defaultMessage: string
  ): ApiErrorClass {
    if (error instanceof ApiErrorClass) {
      return error;
    }

    if (error instanceof Error) {
      return new ApiErrorClass(error.message, 'SERVER_ERROR', 500);
    }

    return new ApiErrorClass(defaultMessage, 'SERVER_ERROR', 500);
  }
}

// =============================================================================
// INSTANCE & FACTORY
// =============================================================================

/** Default report service instance. */
export const reportService = new ReportService();

/** Create a new report service instance. */
export const createReportService = (): ReportService => new ReportService();
